import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { titleCase } from '../../lib/format'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import type { ListResponse, Organization, Truck } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import { CloseIcon, PlusIcon } from '../../components/icons'
import { Wizard } from '../../components/Wizard'

export default function Trucks() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = portalFor(user) === 'admin'
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'trucks'],
    queryFn: () => api.get<ListResponse<Truck>>('/leader/trucks'),
  })
  // super_admin belongs to no cooperative, so registering a truck needs an
  // explicit org picker - see the backend comment on api_leader_truck_create().
  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs', 'picker'],
    queryFn: () => api.get<{ organizations: Organization[] }>('/admin/orgs?status=&limit=1000'),
    enabled: isAdmin && showForm,
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/leader/trucks', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'trucks'] })
      setShowForm(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const capacity = form.get('capacity_tonnes')
    create.mutate({
      org_id: String(form.get('org_id') ?? ''),
      name: String(form.get('name') ?? '').trim(),
      plate: String(form.get('plate') ?? '').trim(),
      kind: String(form.get('kind') ?? 'income'),
      ownership: String(form.get('ownership') ?? 'owned'),
      capacity_tonnes: capacity ? Number(capacity) : undefined,
      driver_name: String(form.get('driver_name') ?? '').trim(),
      driver_phone: String(form.get('driver_phone') ?? '').trim(),
    })
  }

  const trucks = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Fleet</h1>
          <p className="page-head__sub">
            {isAdmin ? 'Trucks used for hauling and dispatch, across every organisation.' : 'Trucks used for hauling and dispatch.'}
          </p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Add truck'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Add truck'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New truck" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not add that truck.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Identity', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        {isAdmin && (
                          <Select label="Organisation" name="org_id" required defaultValue="">
                            <option value="" disabled>Select an organisation…</option>
                            {(orgsQuery.data?.organizations ?? []).map((o) => (
                              <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                          </Select>
                        )}
                        <div className="grid grid--2">
                          <Input label="Name" name="name" required />
                          <Input label="Plate number" name="plate" required />
                        </div>
                      </div>
                    ) },
                    { title: 'Type & capacity', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <div className="grid grid--2">
                          <Select label="Direction" name="kind" defaultValue="income">
                            <option value="income">Income (bringing ore in)</option>
                            <option value="outgoing">Outgoing (taking product out)</option>
                          </Select>
                          <Select label="Ownership" name="ownership" defaultValue="owned">
                            <option value="owned">Owned</option>
                            <option value="hired">Hired</option>
                          </Select>
                        </div>
                        <Input label="Capacity (tonnes)" name="capacity_tonnes" type="number" step="any" min="0" />
                      </div>
                    ) },
                    { title: 'Driver', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <Input label="Driver name" name="driver_name" />
                        <Input label="Driver phone" name="driver_phone" />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending} confirm={{ title: "Add this truck?" }}>Add truck</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Trucks" sub={trucks.length ? `${trucks.length} on file` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : trucks.length === 0 ? (
            <EmptyState title="No trucks yet" body="Add your first truck to start dispatching loads." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Truck</th><th>Plate</th><th>Ownership</th><th>Driver</th><th>Status</th></tr></thead>
                <tbody>
                  {trucks.map((t) => (
                    <tr key={t.id}>
                      <td><Link to={`/trucks/${t.id}`} style={{ fontWeight: 600 }}>{t.name}</Link></td>
                      <td>{t.plate}</td>
                      <td>{titleCase(t.ownership)}</td>
                      <td>{t.driver_name || '—'}</td>
                      <td><StatusBadge status={t.status || 'active'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
